import type { UniqueIdentifier } from '@dnd-kit/core';
import { useEffect, useMemo, useState } from 'react';

/**
 * One entry of an optimistic snapshot: a node's stable `id` plus a `key` that
 * fingerprints its position in the structure — order alone for a list, order +
 * parent for a tree. Two snapshots represent the same structure iff their keys
 * match positionally.
 */
export type SnapshotEntry = {
  id: UniqueIdentifier;
  key: string;
};

/**
 * Fingerprints a `UniqueIdentifier` for a snapshot `key`. Prefixes the runtime
 * type so a numeric `1` and a string `'1'` never collide.
 */
export const idKey = (id: UniqueIdentifier): string => `${typeof id}:${id}`;

type Pending<TEntry extends SnapshotEntry> = {
  /** The structure the committed move should produce. */
  target: TEntry[];
  /**
   * The live structure captured at commit time. It lets us tell "the source of
   * truth hasn't applied the move yet" (still on the baseline) from "the source
   * of truth produced a different structure" (moved off the baseline).
   */
  baseline: TEntry[];
};

const sameIdSet = (
  snapshot: SnapshotEntry[],
  liveIds: Set<UniqueIdentifier>
): boolean =>
  snapshot.length === liveIds.size &&
  snapshot.every((entry) => liveIds.has(entry.id));

const sameStructure = (a: SnapshotEntry[], b: SnapshotEntry[]): boolean =>
  a.length === b.length &&
  a.every((entry, index) => entry.key === b[index].key);

/**
 * Optimistic-snapshot core shared by {@link useOptimisticReorder} (flat list)
 * and `useOptimisticTree` (nested tree). Bridges the one-frame "snap-back" flash
 * that appears when a sortable surface is backed by async source state: the
 * committed move shows immediately and holds until the source of truth reflects
 * it (or overrules it).
 *
 * Call `commit(next)` after a drop to record the optimistic structure, then
 * render from `displayItems` instead of the raw `live` data. `displayItems`
 * re-projects the *live* data onto that structure (via `reapply`), so an
 * in-flight payload edit still flows through.
 *
 * The pending snapshot auto-clears when the live data:
 * - catches up — its structure matches the committed `target` (the move landed);
 * - moves off the commit-time `baseline` — the source of truth produced a
 *   different structure (e.g. the move was rejected or normalised), which wins;
 * - diverges in id set — nodes added/removed, so the snapshot is meaningless.
 *
 * A payload-only edit changes neither the id set nor any structural `key`, so it
 * stays on the baseline and the optimistic structure is preserved.
 */
const useOptimisticSnapshot = <TLive, TEntry extends SnapshotEntry>(
  live: TLive,
  config: {
    /** Flatten the live data into ordered structural entries. */
    snapshot: (live: TLive) => TEntry[];
    /** Re-project the committed structure onto the live payloads. */
    reapply: (target: TEntry[], live: TLive) => TLive;
  }
): { displayItems: TLive; commit: (next: TLive) => void } => {
  const { snapshot, reapply } = config;

  // Flatten the live data once per change; both the display projection and the
  // clear effect read these instead of re-flattening (and re-scanning) each.
  // `snapshot`/`reapply` are expected to be stable (the wrappers pass
  // module-level functions), so listing them never forces a spurious recompute.
  const liveSnapshot = useMemo(() => snapshot(live), [live, snapshot]);
  const liveIds = useMemo(
    () => new Set(liveSnapshot.map((entry) => entry.id)),
    [liveSnapshot]
  );

  const [pending, setPending] = useState<Pending<TEntry> | null>(null);

  const displayItems = useMemo(() => {
    if (!pending || !sameIdSet(pending.target, liveIds)) {
      return live;
    }

    return reapply(pending.target, live);
  }, [live, pending, liveIds, reapply]);

  useEffect(() => {
    if (!pending) {
      return;
    }

    const caughtUp = sameStructure(pending.target, liveSnapshot);
    const movedOffBaseline = !sameStructure(pending.baseline, liveSnapshot);

    if (!sameIdSet(pending.target, liveIds) || caughtUp || movedOffBaseline) {
      setPending(null);
    }
  }, [liveSnapshot, liveIds, pending]);

  const commit = (next: TLive) =>
    setPending({ target: snapshot(next), baseline: liveSnapshot });

  return { displayItems, commit };
};

export default useOptimisticSnapshot;
